import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import {
  getAllUsers,
  createUser,
  updateUser,
  updateUserPassword,
  deleteUser,
  toggleUserStatus,
  getUser,
  getUserPtoHours,
  getUserTwoFactorStatus,
  updateUserTwoFactorStatus,
  updateForcePasswordChange
} from '../../../services/userService';
import { getTwoFactorStatus } from '../../../services/systemSettingsService';
import { getAllTeams } from '../../../services/teamService';
import UserTable from './components/UserTable';
import UserDialog from './components/UserDialog';
import AssignAdminDialog from './components/AssignAdminDialog';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    direction: 'desc', // desc to show active users first
    field: 'isActive'
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [memberTeamDropdownOpen, setMemberTeamDropdownOpen] = useState(false);
  const [ledTeamsDropdownOpen, setLedTeamsDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    firstName: '',
    lastName: '',
    title: '',
    role: 'user',
    memberTeam: [],
    ledTeams: [],
    isActive: true,
    currentTab: 0,
    yearStart: '0',
    ytdAccumulated: '40',
    ytdUsed: '0',
    twoFactorEnabled: false,
    forcePasswordChangeOnLogin: true,
    quickbooksName: '',
    dateOfHire: '',
    payrollType: 'Hourly',
    employmentStatus: 'Full-Time'
  });
  
  // 2FA and password change state
  const [systemTwoFactorEnabled, setSystemTwoFactorEnabled] = useState(false);
  const [twoFactorUpdating, setTwoFactorUpdating] = useState(false);
  const [passwordChangeUpdating, setPasswordChangeUpdating] = useState(false);
  
  // Assign Admin dialog state
  const [openAssignAdminDialog, setOpenAssignAdminDialog] = useState(false);
  const [assignAdminSuccess, setAssignAdminSuccess] = useState(null);

  const fetchUsers = useCallback(async (newSortConfig = sortConfig) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllUsers(page, rowsPerPage, newSortConfig.field, newSortConfig.direction);
      setUsers(data.users);
      setTotalCount(data.pagination.total);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortConfig]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        fetchUsers(),
        fetchTeams(),
        fetchSystemTwoFactorStatus()
      ]);
    };
    init();
  }, [page, rowsPerPage, fetchUsers]); // Re-fetch when pagination changes
  
  const fetchSystemTwoFactorStatus = async () => {
    try {
      const response = await getTwoFactorStatus();
      setSystemTwoFactorEnabled(response.enabled);
    } catch (err) {
      console.error('Error fetching system 2FA status:', err);
    }
  };

  useEffect(() => {
    fetchUsers(sortConfig);
  }, [sortConfig, fetchUsers]); // Re-fetch when sort config changes

  const fetchTeams = async () => {
    try {
      const data = await getAllTeams();
      setTeams(data);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError(err.message || 'Failed to fetch teams');
    }
  };

  const handleOpenDialog = async (user = null) => {
    setError(null);
    setShowPassword(false);
    if (user) {
      try {
        // Fetch user details including password placeholder
        const userDetails = await getUser(user._id);
        
        // Set selected user
        setSelectedUser({
          _id: user._id,
          username: user.username
        });
        
        // Initialize form data with basic user info
        const formDataInit = {
          username: user.username || '',
          password: userDetails.password || '(Set new password to enable visibility)',
          email: user.email || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          title: user.title || '',
          role: user.role || 'user',
          memberTeam: Array.isArray(user.memberTeam) 
            ? user.memberTeam.map(team => team._id) 
            : (user.memberTeam?._id ? [user.memberTeam._id] : []),
          ledTeams: user.ledTeams?.map(team => team._id) || [],
          isActive: user.isActive ?? true,
          currentTab: 0,
          yearStart: '0',
          ytdAccumulated: '40',
          ytdUsed: '0',
          twoFactorEnabled: false,
          // For admin users, default to false regardless of the stored value
          forcePasswordChangeOnLogin: user.role === 'admin' ? false : (user.forcePasswordChangeOnLogin || false),
          quickbooksName: user.quickbooksName || '',
          dateOfHire: user.dateOfHire || '',
          payrollType: user.payrollType || 'Hourly',
          employmentStatus: user.employmentStatus || 'Full-Time',
          passwordChanged: false // Track if password has been changed in this session
        };
        
        // Try to get PTO hours (may fail for team leads if user is not in their team)
        try {
          const ptoHours = await getUserPtoHours(user._id);
          formDataInit.yearStart = ptoHours.yearStart.toString() || '0';
          formDataInit.ytdAccumulated = ptoHours.ytdAccumulated.toString() || '40';
          formDataInit.ytdUsed = ptoHours.ytdUsed.toString() || '0';
        } catch (ptoError) {
          console.error('Error fetching PTO hours:', ptoError);
          // Continue without PTO data
        }
        
        // Try to get 2FA status (may fail for team leads)
        try {
          const twoFactorStatus = await getUserTwoFactorStatus(user._id);
          formDataInit.twoFactorEnabled = twoFactorStatus.twoFactorEnabled || false;
        } catch (twoFaError) {
          console.error('Error fetching 2FA status:', twoFaError);
          // Continue without 2FA data
        }
        
        setFormData(formDataInit);
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError(err.message || 'Failed to fetch user details');
      }
    } else {
      setSelectedUser(null);
      setFormData({
        username: '',
        password: '',
        email: '',
        firstName: '',
        lastName: '',
        title: '',
        role: '',
        memberTeam: [],
        ledTeams: [],
        isActive: true,
        currentTab: 0,
        yearStart: '0',
        ytdAccumulated: '40',
        ytdUsed: '0',
        twoFactorEnabled: false,
        forcePasswordChangeOnLogin: true,
        quickbooksName: '',
        dateOfHire: '',
        payrollType: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setError(null);
    setShowPassword(false);
    setMemberTeamDropdownOpen(false);
    setLedTeamsDropdownOpen(false);
  };

  const handleInputChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    setFormData((prev) => {
      // Handle role changes
      if (name === 'role') {
        if (prev.role === 'team_lead' && value !== 'team_lead') {
          // If changing from team_lead to something else, clear ledTeams
          return {
            ...prev,
            [name]: value,
            ledTeams: [],
            // Set payrollType to N/A for admin and accountant roles
            ...(value === 'admin' || value === 'accountant' ? { payrollType: 'N/A' } : {})
          };
        } else if (prev.role !== 'team_lead' && value === 'team_lead') {
          // If changing to team_lead, initialize empty ledTeams array if not exists
          return {
            ...prev,
            [name]: value,
            ledTeams: prev.ledTeams || []
          };
        } else if (value === 'admin' || value === 'accountant') {
          // Set payrollType to N/A for admin and accountant roles
          // Also set forcePasswordChangeOnLogin to false for admin users
          return {
            ...prev,
            [name]: value,
            payrollType: 'N/A',
            ...(value === 'admin' ? { forcePasswordChangeOnLogin: false } : {})
          };
        }
      }
      
      // If password is being changed, enable visibility and mark as changed
      if (name === 'password' && selectedUser) {
        setShowPassword(true); // Automatically show password when it's being changed
        return {
          ...prev,
          [name]: value,
          passwordChanged: true
        };
      }
      
      return {
        ...prev,
        [name]: value,
      };
    });
  };
  
  // Function to close the dropdown menu
  const handleCloseDropdown = (event) => {
    // This prevents the dropdown from closing when clicking inside it
    event.stopPropagation();
  };

  // Validate password against requirements
  const validatePassword = (password) => {
    if (!password || password === 'Enter new password to update') {
      return { valid: false, error: 'Password is required' };
    }
    
    if (password.length < 12) {
      return { valid: false, error: 'Password must be at least 12 characters long' };
    }
    
    const digitCount = (password.match(/\d/g) || []).length;
    if (digitCount < 1) {
      return { valid: false, error: 'Password must contain at least 1 digit' };
    }
    
    const specialCharCount = (password.match(/[!@#$%^&*(),.?":{}|<>-]/g) || []).length;
    if (specialCharCount < 1) {
      return { valid: false, error: 'Password must contain at least 1 special character' };
    }
    
    return { valid: true };
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      setError(null);
      const submissionData = { ...formData };
      delete submissionData.currentTab;

      Object.keys(submissionData).forEach(key => {
        if (submissionData[key] === '' || submissionData[key] === undefined) {
          delete submissionData[key];
        }
      });

      if (selectedUser?._id) {
        // Remove username, password, and ytdUsed from update data
        // ytdUsed is now automatically calculated and should not be manually updated
        const { username, password, passwordChanged, ytdUsed, ...updateData } = submissionData;
        
        const response = await updateUser(selectedUser._id, updateData);
        
        // If password was provided and changed, update it separately
        if (password && passwordChanged && password !== 'Enter new password to update') {
          // Validate password before updating
          const passwordValidation = validatePassword(password);
          if (!passwordValidation.valid) {
            setError(passwordValidation.error);
            return;
          }
          
          await updateUserPassword(selectedUser._id, password);
        }
      } else {
        // Validate password for new user
        const passwordValidation = validatePassword(submissionData.password);
        if (!passwordValidation.valid) {
          setError(passwordValidation.error);
          return;
        }
        
        // Force payrollType to N/A for admin and accountant roles
        if (submissionData.role === 'admin' || submissionData.role === 'accountant') {
          submissionData.payrollType = 'N/A';
        }
        
        const response = await createUser(submissionData);
      }

      // Reset dropdown states and close dialog
      setMemberTeamDropdownOpen(false);
      setLedTeamsDropdownOpen(false);
      handleCloseDialog();
      
      await Promise.all([
        fetchUsers(),
        fetchTeams() // Refresh teams data to update led teams display
      ]);
    } catch (err) {
      console.error('Error in form submission:', err);
      
      // Extract specific error message if available
      let errorMessage = err.message || 'Failed to save user';
      
      // Check for specific password-related errors
      if (errorMessage.includes('Password')) {
        if (errorMessage.includes('12 characters')) {
          errorMessage = 'Password must be at least 12 characters long';
        } else if (errorMessage.includes('1 digit')) {
          errorMessage = 'Password must contain at least 1 digit';
        } else if (errorMessage.includes('special character')) {
          errorMessage = 'Password must contain at least 1 special character';
        }
      }
      
      setError(errorMessage);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setError(null);
        await deleteUser(userId);
        fetchUsers();
      } catch (err) {
        setError(err.message || 'Failed to delete user');
        console.error('Error deleting user:', err);
      }
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      setError(null);
      await toggleUserStatus(userId, !currentStatus);
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to toggle user status');
      console.error('Error toggling user status:', err);
    }
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
      field
    }));
  };

  const handleTwoFactorToggle = async (e) => {
    try {
      setTwoFactorUpdating(true);
      setError(null);
      
      const newStatus = e.target.checked;
      await updateUserTwoFactorStatus(selectedUser._id, newStatus);
      
      setFormData(prev => ({
        ...prev,
        twoFactorEnabled: newStatus
      }));
      
    } catch (err) {
      setError(err.message || 'Failed to update 2FA status');
      console.error('Error updating 2FA status:', err);
    } finally {
      setTwoFactorUpdating(false);
    }
  };

  const handleForcePasswordChangeToggle = async (e) => {
    try {
      setPasswordChangeUpdating(true);
      setError(null);
      
      const newStatus = e.target.checked;
      await updateForcePasswordChange(selectedUser._id, newStatus);
      
      setFormData(prev => ({
        ...prev,
        forcePasswordChangeOnLogin: newStatus
      }));
      
    } catch (err) {
      setError(err.message || 'Failed to update password change requirement');
      console.error('Error updating password change requirement:', err);
    } finally {
      setPasswordChangeUpdating(false);
    }
  };

  // Assign Admin dialog handlers
  const handleOpenAssignAdminDialog = () => {
    setOpenAssignAdminDialog(true);
  };

  const handleCloseAssignAdminDialog = () => {
    setOpenAssignAdminDialog(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      '@media (max-width:600px)': {
        p: 1, // Much more reduced padding for mobile
        paddingTop: 0, // No top padding - layout provides spacing
        paddingBottom: '88px', // Account for bottom navigation
      },
      '@media (max-width:900px) and (orientation: landscape)': {
        p: 0.5, // Minimal padding in landscape mode
        paddingTop: 0, // No top padding
        paddingBottom: 0.5, // Minimal bottom padding
        height: 'auto', // Allow natural height
        overflow: 'visible', // Allow content to overflow
        minHeight: 'auto', // Remove height constraints
      }
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        '@media (max-width:600px)': {
          flexDirection: 'column',
          gap: 2,
        }
      }}>
        {/* Success notification */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          {assignAdminSuccess && (
            <Alert 
              severity="success"
              onClose={() => setAssignAdminSuccess(null)}
              sx={{ width: 'auto' }}
            >
              {assignAdminSuccess}
            </Alert>
          )}
        </Box>
        
        {/* Buttons */}
        <Box sx={{
          '@media (max-width:600px)': {
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
          }
        }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAssignAdminDialog}
            sx={{ 
              mr: 2,
              '@media (max-width:600px)': {
                fontSize: '0.875rem',
                padding: '8px 16px',
                mr: 0,
              }
            }}
          >
            Assign Admin
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              '@media (max-width:600px)': {
                fontSize: '0.875rem',
                padding: '8px 16px',
              }
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      <UserTable
        users={users}
        sortConfig={sortConfig}
        handleSort={handleSort}
        handleOpenDialog={handleOpenDialog}
        handleDelete={handleDelete}
        handleToggleStatus={handleToggleStatus}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        error={error}
      />

      <UserDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        selectedUser={selectedUser}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        teams={teams}
        memberTeamDropdownOpen={memberTeamDropdownOpen}
        setMemberTeamDropdownOpen={setMemberTeamDropdownOpen}
        ledTeamsDropdownOpen={ledTeamsDropdownOpen}
        setLedTeamsDropdownOpen={setLedTeamsDropdownOpen}
        handleCloseDropdown={handleCloseDropdown}
        systemTwoFactorEnabled={systemTwoFactorEnabled}
        twoFactorUpdating={twoFactorUpdating}
        passwordChangeUpdating={passwordChangeUpdating}
        handleTwoFactorToggle={handleTwoFactorToggle}
        handleForcePasswordChangeToggle={handleForcePasswordChangeToggle}
        error={error}
      />

      <AssignAdminDialog
        open={openAssignAdminDialog}
        handleClose={handleCloseAssignAdminDialog}
        onSuccess={() => {
          fetchUsers();
          // Show success message only after successful assignment
          setAssignAdminSuccess("Administrative project temporarily assigned successfully");
        }}
      />
    </Box>
  );
};

export default Users;
